// PanelThemeProvider: light by default, dark through the toggle, the viewer's choice kept in localStorage.
import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { cssVarsFor, muiThemeFor, normalizeScheme, DEFAULT_SCHEME, STORAGE_KEY } from './united';

export function readStoredScheme() {
    try {
        return normalizeScheme(window.localStorage.getItem(STORAGE_KEY));
    } catch (e) {
        return DEFAULT_SCHEME;
    }
}

function writeStoredScheme(scheme) {
    try {
        window.localStorage.setItem(STORAGE_KEY, scheme);
    } catch (e) {
        // storage unavailable (private mode, blocked): the choice lasts for this page only
    }
}

export function applySchemeToDocument(scheme) {
    const root = document.documentElement;
    Object.entries(cssVarsFor(scheme)).forEach(([name, value]) => root.style.setProperty(name, value));
    root.setAttribute('data-theme', scheme);
    root.style.colorScheme = scheme;
    root.style.backgroundColor = 'var(--bg)'; // replaces the pre-paint colour set in public/index.html
}

const PanelThemeContext = createContext({ scheme: DEFAULT_SCHEME, setScheme: () => {}, toggleScheme: () => {} });

export function PanelThemeProvider({ children }) {
    const [scheme, setSchemeState] = useState(readStoredScheme);

    useLayoutEffect(() => { applySchemeToDocument(scheme); }, [scheme]);

    const setScheme = useCallback((next) => {
        const s = normalizeScheme(next);
        writeStoredScheme(s);
        setSchemeState(s);
    }, []);
    const toggleScheme = useCallback(() => setScheme(scheme === 'dark' ? 'light' : 'dark'), [scheme, setScheme]);

    const muiTheme = useMemo(() => muiThemeFor(scheme), [scheme]);
    const value = useMemo(() => ({ scheme, setScheme, toggleScheme }), [scheme, setScheme, toggleScheme]);

    return (
        <PanelThemeContext.Provider value={value}>
            <ThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </PanelThemeContext.Provider>
    );
}

export const usePanelTheme = () => useContext(PanelThemeContext);
